/**
 * Reads the connection snippet MCP providers publish for Claude, Cursor or
 * Windsurf and turns it into our fields — used when someone pastes such a
 * snippet into the address field (there is no separate paste box). Supported
 * shapes:
 *
 *   { "mcpServers": { "<name>": { "url": "...", "headers": { ... } } } }   Claude / Cursor
 *   { "mcpServers": { "<name>": { "serverUrl": "...", ... } } }             Windsurf
 *   { "<name>": { "url": ... } }  or  { "url": ..., "headers": ... }       fragments
 *   { "command": "npx", "args": ["mcp-remote", "https://…", "--header", "Authorization: Bearer …"] }
 *
 * Only remote servers are possible (no local process runs on our side), so a
 * stdio entry without a URL is reported as such.
 */

/**
 * How the key reaches the server. Covers what remote MCP servers ask for:
 * `Authorization: Bearer <key>` (most), `X-API-Key`, `Api-Key`, a bare
 * `Authorization: <key>`, or some other header (`custom`, name typed in).
 */
export type McpAuthPreset = 'none' | 'bearer' | 'x-api-key' | 'api-key' | 'authorization' | 'custom';

/** Header each preset sends; null for none / bearer / custom. */
export const PRESET_HEADER: Partial<Record<McpAuthPreset, string>> = {
  'x-api-key': 'X-API-Key',
  'api-key': 'Api-Key',
  authorization: 'Authorization',
};

export interface ParsedMcpConfig {
  name?: string;
  url?: string;
  preset: McpAuthPreset;
  headerName?: string;
  /** '' when the snippet only had a placeholder like <YOUR_API_KEY> */
  secret: string;
  /** i18n keys under pages.mcp.paste.* */
  notes: string[];
}

const PLACEHOLDER = /^<.*>$|your[_ -]?(api[_ -]?)?key|xxx|\.\.\./i;

export function presetForHeader(name: string, value: string): { preset: McpAuthPreset; secret: string; headerName?: string } {
  const lower = name.trim().toLowerCase();
  let secret = value.trim();
  if (lower === 'authorization') {
    if (/^bearer\s+/i.test(secret)) {
      return { preset: 'bearer', secret: secret.replace(/^bearer\s+/i, '') };
    }
    return { preset: 'authorization', secret };
  }
  if (lower === 'x-api-key') return { preset: 'x-api-key', secret };
  if (lower === 'api-key') return { preset: 'api-key', secret };
  return { preset: 'custom', headerName: name.trim(), secret };
}

export function parseMcpConfig(text: string): ParsedMcpConfig | null {
  let data: any;
  try {
    data = JSON.parse(text.trim());
  } catch {
    // A bare URL is fine too.
    const url = text.trim();
    return /^https?:\/\/\S+$/i.test(url) ? { url, preset: 'none', secret: '', notes: [] } : null;
  }
  if (!data || typeof data !== 'object') return null;

  let name: string | undefined;
  let entry: any = data;
  const servers = data.mcpServers ?? data.servers ?? data.mcp?.servers;
  if (servers && typeof servers === 'object') {
    const keys = Object.keys(servers);
    if (!keys.length) return null;
    name = keys[0];
    entry = servers[name];
  } else if (!('url' in data) && !('serverUrl' in data) && !('command' in data)) {
    const keys = Object.keys(data);
    if (keys.length === 1 && typeof data[keys[0]] === 'object') {
      name = keys[0];
      entry = data[keys[0]];
    }
  }

  const notes: string[] = [];
  if (servers && Object.keys(servers).length > 1) notes.push('multipleServers');

  let url: string | undefined = entry?.url ?? entry?.serverUrl;
  const headers: Record<string, string> = { ...(entry?.headers ?? {}) };

  // npx mcp-remote https://… --header "Authorization: Bearer …"
  if (!url && Array.isArray(entry?.args)) {
    const args: string[] = entry.args.map(String);
    url = args.find((a) => /^https:\/\//i.test(a));
    args.forEach((a, i) => {
      if (a === '--header' && args[i + 1]?.includes(':')) {
        const [h, ...rest] = args[i + 1].split(':');
        headers[h.trim()] = rest.join(':').trim();
      }
    });
  }
  if (!url) {
    notes.push(entry?.command ? 'localOnly' : 'noUrl');
    return { name, preset: 'none', secret: '', notes };
  }

  const headerNames = Object.keys(headers);
  if (headerNames.length > 1) notes.push('multipleHeaders');
  let result: ParsedMcpConfig = { name, url, preset: 'none', secret: '', notes };
  if (headerNames.length) {
    const h = headerNames[0];
    const mapped = presetForHeader(h, String(headers[h] ?? ''));
    result = { ...result, ...mapped };
    // Resolve ${ENV} style values too — they are placeholders here.
    if (!result.secret || PLACEHOLDER.test(result.secret) || /^\$\{.*\}$/.test(result.secret)) {
      result.secret = '';
      notes.push('enterKey');
    }
  }
  return result;
}
